/**
 * Themed surface helpers shared by section renderers (footer, navbar, future themed sections).
 * A "themed surface" is a section with a chooseable background — either the site
 * primary color or a user-picked custom hex — paired with auto-contrast text colors.
 */

import { getReadableTextColor, isValidHexColor } from "@/features/landing-pages/_logic/color-utils";

/** Resolved colors for a themed background — text + muted text + border auto-contrast against the bg. */
export interface ThemedSurface {
  backgroundColor: string;
  textColor: string;
  mutedTextColor: string;
  borderColor: string;
}

/** Inputs for `resolveThemedSurface`. `customColor` may be null/undefined; falls back to `defaultCustomColor`. */
export interface ResolveThemedSurfaceInput {
  mode: "primary" | "custom";
  customColor: string | null | undefined;
  primaryColor: string;
  defaultCustomColor: string;
}

/**
 * Resolves background + auto-contrast text/border colors for a themed surface.
 * @param input.mode - "primary" uses the site's primary color, "custom" uses the validated hex
 * @param input.customColor - User-picked hex (validated; falls back to `defaultCustomColor` if invalid/empty)
 * @param input.primaryColor - Site primary color (used when mode === "primary")
 * @param input.defaultCustomColor - Fallback for an invalid/empty `customColor`
 * @returns Background + textColor + mutedTextColor + borderColor (~20% alpha of textColor)
 */
export function resolveThemedSurface({
  mode,
  customColor,
  primaryColor,
  defaultCustomColor,
}: ResolveThemedSurfaceInput): ThemedSurface {
  const safeCustom = customColor && isValidHexColor(customColor) ? customColor : defaultCustomColor;
  const backgroundColor = mode === "custom" ? safeCustom : primaryColor;
  const textColor = getReadableTextColor("#ffffff", backgroundColor);
  const mutedTextColor = getReadableTextColor("#cbd5e1", backgroundColor);
  const borderColor = `${textColor}33`; // ~20% alpha
  return { backgroundColor, textColor, mutedTextColor, borderColor };
}
